import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, PlayCircle, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FILTERS = [["", "All"], ["requested", "Requested"], ["approved", "Approved"], ["paid", "Paid"], ["rejected", "Rejected"], ["failed", "Failed"]];

export default function Withdrawals() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const { data } = useQuery({
    queryKey: ["withdrawals", status],
    queryFn: async () => (await api.get(`/admin/withdrawals${status ? `?status=${status}` : ""}`)).data,
  });

  const approve = useMutation({
    mutationFn: async (id) => api.post(`/admin/withdrawals/${id}/approve`, {}),
    onSuccess: () => { toast.success("Approved — queued for the daily payout batch"); qc.invalidateQueries({ queryKey: ["withdrawals"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const reject = useMutation({
    mutationFn: async () => api.post(`/admin/withdrawals/${rejectFor.id}/reject`, { reason }),
    onSuccess: () => { toast.success("Rejected"); setRejectFor(null); setReason(""); qc.invalidateQueries({ queryKey: ["withdrawals"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const runBatch = useMutation({
    mutationFn: async () => (await api.post("/admin/payouts/run", {})).data,
    onSuccess: (d) => {
      toast.success(`Payout batch complete: ${d.paid} paid, ${d.failed} failed (${d.mode} mode)`);
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="admin-withdrawals-page">
      <PageHeader title="Withdrawals & Payouts" sub="Approve agent withdrawals — the daily batch pays approved ones via Razorpay (mock until keys are set)." testid="withdrawals-title">
        <Button data-testid="run-payout-batch-btn" onClick={() => runBatch.mutate()} disabled={runBatch.isPending}
          className="bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          <PlayCircle size={14} /> {runBatch.isPending ? "Running…" : "Run payout batch now"}
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`wd-filter-${v || "all"}`} onClick={() => setStatus(v)}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${status === v ? "border-[#FF6B00] bg-orange-50 text-[#E85D04]" : "border-slate-200 text-slate-500"}`}>
            {l}
          </button>
        ))}
      </div>

      {!data?.items?.length ? <EmptyState title="No withdrawal requests" sub="Agent withdrawal requests appear here for approval." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white shadow-sm">
          <table className="w-full text-left text-xs" data-testid="withdrawals-table">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Bank</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payout</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((w) => (
                <tr key={w.id} data-testid={`wd-row-${w.id}`} className="border-b border-slate-50">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(w.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-[#0F2440]">{w.agent_name}</td>
                  <td className="px-4 py-3 font-semibold text-[#0F2440]">{fmtINR(w.amount)}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {w.bank ? <span className="flex items-center gap-1"><Landmark size={11} /> {w.bank.bank_name || "Bank"} ····{String(w.bank.account || "").slice(-4)} <span className="font-mono text-[10px]">{w.bank.ifsc}</span></span> : "—"}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={w.status} />{w.reason && <p className="mt-0.5 text-[10px] text-rose-500">{w.reason}</p>}</td>
                  <td className="px-4 py-3 font-mono text-[10px] text-slate-500">{w.payout_id || "—"}{w.payout_mode === "mock" && <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] font-bold text-amber-700">MOCK</span>}</td>
                  <td className="px-4 py-3">
                    {w.status === "requested" && (
                      <div className="flex gap-1.5">
                        <Button data-testid={`wd-approve-${w.id}`} size="sm" onClick={() => approve.mutate(w.id)} disabled={approve.isPending}
                          className="h-7 bg-emerald-500 text-[10px] font-bold text-white hover:bg-emerald-600"><CheckCircle2 size={11} /> Approve</Button>
                        <Button data-testid={`wd-reject-${w.id}`} size="sm" variant="outline" onClick={() => setRejectFor(w)}
                          className="h-7 border-rose-200 text-[10px] text-rose-600 hover:bg-rose-50"><XCircle size={11} /> Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
        <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>Reject withdrawal — {fmtINR(rejectFor?.amount)}</DialogTitle></DialogHeader>
          <Input data-testid="wd-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (shared with agent)" className="border-slate-200 bg-white" />
          <Button data-testid="wd-reject-confirm" onClick={() => reject.mutate()} disabled={reject.isPending}
            className="w-full bg-rose-500 font-semibold text-white hover:bg-rose-600">Reject withdrawal</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
